/**
 * Razorpay Checkout integration helpers.
 *
 * The Checkout widget is loaded on demand (not bundled) by injecting the
 * official script the first time the user confirms a purchase. We avoid a
 * hard dependency and keep the initial bundle lean.
 *
 * Production builds load the public live Key ID from `.env.production`.
 * Local development may still use the `rzp_test_placeholder` fallback, which
 * must NOT be treated as usable. `paymentsEnabled()` gates the flow on both a
 * real key and the explicit build-time flag.
 */

const SCRIPT_SRC = "https://checkout.razorpay.com/v1/checkout.js";

/** Values that mean "no real key configured yet". */
const PLACEHOLDER_KEYS = new Set([
  "",
  "rzp_test_placeholder",
  "rzp_test_xxxxxxxxxxxxxx",
]);

const TRUE_VALUES = new Set(["1", "true", "yes", "on"]);

/** The raw key from Vite env, trimmed. String() guards the `any`-typed env. */
export function razorpayKeyId(): string {
  const raw = import.meta.env.VITE_RAZORPAY_KEY_ID;
  return (raw == null ? "" : String(raw)).trim();
}

function paymentsFlagEnabled(): boolean {
  const raw = import.meta.env.VITE_PAYMENTS_ENABLED;
  return TRUE_VALUES.has((raw == null ? "" : String(raw)).trim().toLowerCase());
}

function isCanonicalProductionHost(): boolean {
  return typeof window !== "undefined" && window.location.hostname === "app.staxed.in";
}

/**
 * True only when a usable Razorpay key is configured. Treats the env default
 * placeholder and the .env.example sample value as "not enabled" so the UI can
 * show a graceful "Payments aren't enabled yet" state instead of opening a
 * broken Checkout.
 */
export function paymentsEnabled(): boolean {
  const key = razorpayKeyId();
  if (!paymentsFlagEnabled()) return false;
  if (key.length === 0 || PLACEHOLDER_KEYS.has(key.toLowerCase())) return false;

  // A public app.staxed.in build must never open Razorpay in Test Mode. This
  // lets us deploy policy pages for Razorpay verification before the live
  // merchant account is ready without giving users test-payment entitlements.
  if (isCanonicalProductionHost() && !key.startsWith("rzp_live_")) return false;

  return true;
}

/**
 * Razorpay's `theme.color` only accepts a hex string -- it can't read our CSS
 * tokens. Resolve the live `--brand` variable (space-separated RGB triplet) to
 * a hex so the Checkout accent tracks the PULSE brand token instead of a magic
 * literal. Falls back to the pulse-500 brand if the variable is unreadable.
 */
export function brandHex(): string {
  const fallback = "#f97316";
  if (typeof document === "undefined") return fallback;
  const raw = getComputedStyle(document.documentElement)
    .getPropertyValue("--brand")
    .trim();
  const parts = raw.split(/\s+/).map((n) => Number(n));
  if (parts.length !== 3 || parts.some((n) => !Number.isFinite(n))) return fallback;
  return (
    "#" +
    parts
      .map((n) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, "0"))
      .join("")
  );
}

// ---------------------------------------------------------------------------
// Razorpay Checkout type surface (only the bits we use).
// ---------------------------------------------------------------------------
export type RazorpayHandlerResponse = {
  razorpay_payment_id: string;
  razorpay_order_id: string;
  razorpay_signature: string;
};

export type RazorpayOptions = {
  key: string;
  amount: number;
  currency: string;
  name: string;
  description?: string;
  order_id: string;
  prefill?: { name?: string; email?: string; contact?: string };
  notes?: Record<string, string>;
  theme?: { color?: string };
  handler: (response: RazorpayHandlerResponse) => void;
  modal?: { ondismiss?: () => void };
};

type RazorpayFailureResponse = {
  error?: { description?: string; reason?: string; code?: string };
};

export type RazorpayInstance = {
  open: () => void;
  on: (event: "payment.failed", cb: (response: RazorpayFailureResponse) => void) => void;
};

type RazorpayConstructor = new (options: RazorpayOptions) => RazorpayInstance;

declare global {
  interface Window {
    Razorpay?: RazorpayConstructor;
  }
}

let loadPromise: Promise<RazorpayConstructor> | null = null;

/**
 * Inject the Razorpay Checkout script once and resolve with the constructor.
 * Memoised -- repeated calls reuse the in-flight / resolved promise.
 */
export function loadRazorpayCheckout(): Promise<RazorpayConstructor> {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("Razorpay can only load in the browser"));
  }
  if (window.Razorpay) {
    return Promise.resolve(window.Razorpay);
  }
  if (loadPromise) return loadPromise;

  loadPromise = new Promise<RazorpayConstructor>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${SCRIPT_SRC}"]`,
    );
    const onLoad = () => {
      if (window.Razorpay) resolve(window.Razorpay);
      else reject(new Error("Razorpay script loaded but constructor missing"));
    };
    if (existing) {
      existing.addEventListener("load", onLoad, { once: true });
      existing.addEventListener(
        "error",
        () => reject(new Error("Failed to load Razorpay")),
        { once: true },
      );
      // If it already loaded before we attached, resolve immediately.
      if (window.Razorpay) resolve(window.Razorpay);
      return;
    }
    const script = document.createElement("script");
    script.src = SCRIPT_SRC;
    script.async = true;
    script.onload = onLoad;
    script.onerror = () => {
      loadPromise = null; // allow a retry on next attempt
      reject(new Error("Failed to load Razorpay"));
    };
    document.body.appendChild(script);
  });

  return loadPromise;
}
